import { useCallback, useEffect, useMemo } from 'react';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import {
	Facebook,
	Twitter,
	WhatsApp,
} from '@sy/app-client/src/ssrAdapter/components/CustomIcons';
import {
	Email,
	Close,
	Share,
} from '@sy/app-client/src/ssrAdapter/components/MaterialIcons';
import { mixins } from '@sy/component-library';
import { useEditableModal, useMediaQuery } from '~/utils/hooks';
import { events } from '~/services/tracking';
import {
	BigScreenWrapper,
	ShareButtonIcon,
	SmallScreenButtonsList,
	SmallScreenButtonsWrapper,
	SmallScreenShareButton,
	SmallScreenWrapper,
	StyledEmailShareButton,
	StyledFacebookShareButton,
	StyledTwitterShareButton,
	StyledWhatsappShareButton,
} from './SocialShare.styles';

const { mediaQueries } = mixins;

const SocialShare = ({ contentName, pageUrl }: Props) => {
	const { isOpen, close, toggle } = useEditableModal();

	const isLargeScreen = useMediaQuery(mediaQueries.large);

	const trackShareClick = useCallback(
		(shareType: string) => {
			analytics.track(events.SHARE_CLICKED, {
				contentName,
				shareType,
			});
		},
		[contentName],
	);

	const shareButtons = useMemo(
		() => ({
			email: (
				<StyledEmailShareButton
					beforeOnClick={() => trackShareClick('email')}
					url={pageUrl}
				>
					<ShareButtonIcon>
						<Email size={29} />
					</ShareButtonIcon>
				</StyledEmailShareButton>
			),
			facebook: (
				<StyledFacebookShareButton
					onShareWindowClose={() => trackShareClick('facebook')}
					url={pageUrl}
				>
					<ShareButtonIcon>
						<Facebook size={29} />
					</ShareButtonIcon>
				</StyledFacebookShareButton>
			),
			twitter: (
				<StyledTwitterShareButton
					onShareWindowClose={() => trackShareClick('twitter')}
					url={pageUrl}
				>
					<ShareButtonIcon>
						<Twitter size={29} />
					</ShareButtonIcon>
				</StyledTwitterShareButton>
			),
			whatsapp: (
				<StyledWhatsappShareButton
					onShareWindowClose={() => trackShareClick('whatsapp')}
					url={pageUrl}
				>
					<ShareButtonIcon>
						<WhatsApp size={29} />
					</ShareButtonIcon>
				</StyledWhatsappShareButton>
			),
		}),
		[pageUrl, trackShareClick],
	);
	useEffect(() => {
		close();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLargeScreen]);

	return isLargeScreen ? (
		<BigScreenWrapper>
			{shareButtons.email}
			{shareButtons.facebook}
			{shareButtons.twitter}
			{shareButtons.whatsapp}
		</BigScreenWrapper>
	) : (
		<SmallScreenWrapper>
			<SmallScreenShareButton onClick={toggle}>
				{isOpen ? <Close /> : <Share />}
			</SmallScreenShareButton>
			<SmallScreenButtonsWrapper expanded={isOpen}>
				<SmallScreenButtonsList>
					{shareButtons.whatsapp}
					{shareButtons.twitter}
					{shareButtons.facebook}
					{shareButtons.email}
				</SmallScreenButtonsList>
			</SmallScreenButtonsWrapper>
		</SmallScreenWrapper>
	);
};

type Props = {
	contentName?: string;
	pageUrl: string;
};

export default SocialShare;
